import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { login as apiLogin, request } from '@/api';

interface User {
  name: string;
  email: string;
  role: string;
}

interface AuthContextType {
  token: string | null;
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>(null!);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('babuba_token'));
  const [user, setUser] = useState<User | null>(() => {
    const u = localStorage.getItem('babuba_user');
    return u ? JSON.parse(u) : null;
  });
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);

  // Validate stored token on mount — if server restarted, stale tokens get cleared
  useEffect(() => {
    const storedToken = localStorage.getItem('babuba_token');
    if (!storedToken) {
      setValidatingToken(false);
      return;
    }
    request<any>('/me', { method: 'GET' })
      .then((session) => {
        // Token is valid, restore user if available
        if (!user) setUser({ name: session.name || '', email: session.email || '', role: 'admin' });
        setValidatingToken(false);
      })
      .catch(() => {
        // Token invalid — server was restarted. Clear and show login.
        setToken(null);
        setUser(null);
        localStorage.removeItem('babuba_token');
        localStorage.removeItem('babuba_user');
        setValidatingToken(false);
      });
  }, []);

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const data = await apiLogin(email, password);
      setToken(data.token);
      const u: User = { name: data.name || '', email: data.email || email, role: data.role || 'admin' };
      setUser(u);
      localStorage.setItem('babuba_user', JSON.stringify(u));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    // Tell the server to re-lock core access (sign-off) — best-effort, the
    // token is cleared regardless.
    try {
      fetch('/api/core/lock', {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('babuba_token') || ''}`, 'Content-Type': 'application/json' },
      }).catch(() => {});
    } catch { /* best-effort */ }
    setToken(null);
    setUser(null);
    localStorage.removeItem('babuba_token');
    localStorage.removeItem('babuba_user');
  };

  // Show loading spinner while validating token
  if (validatingToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <span className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-500 text-sm dark:text-slate-400">Validating session...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ token, user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
